const cognitoRegion = import.meta.env.VITE_COGNITO_REGION || 'eu-west-1'
const ENDPOINT = `https://cognito-idp.${cognitoRegion}.amazonaws.com/`

async function cognitoPost(target, body) {
  const resp = await fetch(ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-amz-json-1.1',
      'X-Amz-Target': `AWSCognitoIdentityProviderService.${target}`,
    },
    body: JSON.stringify(body),
  })
  const data = await resp.json()
  if (!resp.ok) {
    throw new Error(data.message || data.__type || `${target} failed`)
  }
  return data
}

/**
 * Start TOTP setup — returns a secret code for the authenticator app.
 * @param {string} accessToken - Cognito access token
 * @returns {Promise<string>} The TOTP secret code
 */
export async function associateTotpToken(accessToken) {
  const data = await cognitoPost('AssociateSoftwareToken', {
    AccessToken: accessToken,
  })
  return data.SecretCode
}

/**
 * Verify the TOTP code and activate MFA for the user.
 * @param {string} accessToken - Cognito access token
 * @param {string} code - 6-digit TOTP code from authenticator app
 * @returns {Promise<void>}
 */
export async function verifyAndEnableTotp(accessToken, code) {
  await cognitoPost('VerifySoftwareToken', {
    AccessToken: accessToken,
    UserCode: code,
    FriendlyDeviceName: 'Soundcheck',
  })
  await cognitoPost('SetUserMFAPreference', {
    AccessToken: accessToken,
    SoftwareTokenMfaSettings: {
      Enabled: true,
      PreferredMfa: true,
    },
  })
}

/**
 * Disable TOTP MFA for the user.
 * @param {string} accessToken - Cognito access token
 * @returns {Promise<void>}
 */
export async function disableTotp(accessToken) {
  await cognitoPost('SetUserMFAPreference', {
    AccessToken: accessToken,
    SoftwareTokenMfaSettings: {
      Enabled: false,
      PreferredMfa: false,
    },
  })
}

/**
 * Check if TOTP MFA is enabled for the user.
 * @param {string} accessToken - Cognito access token
 * @returns {Promise<boolean>}
 */
export async function isTotpEnabled(accessToken) {
  const data = await cognitoPost('GetUser', {
    AccessToken: accessToken,
  })
  return (data.UserMFASettingList || []).includes('SOFTWARE_TOKEN_MFA')
}

/**
 * Build an otpauth:// URI for QR code generation.
 * @param {string} secret - TOTP secret code
 * @param {string} email - User's email (used as account name)
 * @returns {string} otpauth URI
 */
/**
 * Change the user's password.
 * @param {string} accessToken - Cognito access token
 * @param {string} previousPassword - Current password
 * @param {string} proposedPassword - New password
 * @returns {Promise<void>}
 */
export async function changePassword(accessToken, previousPassword, proposedPassword) {
  await cognitoPost('ChangePassword', {
    AccessToken: accessToken,
    PreviousPassword: previousPassword,
    ProposedPassword: proposedPassword,
  })
}

export function buildTotpUri(secret, email) {
  const issuer = 'Soundcheck'
  return `otpauth://totp/${encodeURIComponent(issuer)}:${encodeURIComponent(email)}?secret=${secret}&issuer=${encodeURIComponent(issuer)}&digits=6&period=30`
}
